import { Injectable, inject } from '@angular/core';
import { Module } from '@library/store/modules/modules.interface';
import { WeathermapFacade } from '@store/weathermap.facade';
import { FilterSpecification, GeoJSONSource, VectorTileSource } from 'maplibre-gl';
import { FavoriteLayers, FriendLayers, HomeLayers, Layers, SelectedLayer, ZoomFilter } from 'src/app/weathermap.const';

@Injectable({
  providedIn: 'root',
})
export class LayersService {
  private readonly weathermapFacade = inject(WeathermapFacade);

  // Reload layers to remove and reload all the pins
  reloadLayers(): void {
    const pinType = this.weathermapFacade.selectedPintype();
    const hasLayer = (layerId: string): boolean => window.map.getStyle().layers.some((l) => l.id === layerId);

    if (hasLayer('stations')) {
      window.map.removeLayer('stations');
      const source: VectorTileSource = window.map.getSource('stations');
      source.setUrl(`NAProtocol://${pinType}`);
      window.map.addLayer(Layers[pinType]);
    }

    if (hasLayer('home-stations')) {
      window.map.removeLayer('home-stations');
      window.map.addLayer(HomeLayers[pinType]);
    }

    if (hasLayer('friend-stations')) {
      window.map.removeLayer('friend-stations');
      window.map.addLayer(FriendLayers[pinType]);
    }

    if (hasLayer('favorite-stations')) {
      window.map.removeLayer('favorite-stations');
      window.map.addLayer(FavoriteLayers[pinType]);
    }
  }

  setGeoJSONData(sourceId: string, stations: Module[], modules: Module[]): void {
    window.map.getSource<GeoJSONSource>(sourceId)?.setData({
      type: 'FeatureCollection',
      features: stations
        .filter((station) => station.modules_bridged)
        .map((station) => {
          const stationModules = modules.filter((module) => station.modules_bridged?.includes(module.id));
          const outdoorModule = stationModules.find((m) => m.type === 'NAModule1');
          const windModule = stationModules.find((m) => m.type === 'NAModule2');
          const rainModule = stationModules.find((m) => m.type === 'NAModule3');

          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: station.coordinates,
            },
            properties: {
              id: station.id,
              city: station.city,
              altitude: station.altitude,
              name: station.name,

              modules: JSON.stringify([outdoorModule, rainModule, windModule].filter((m) => !!m)),

              temperature: Math.round(outdoorModule?.Temperature),

              rain_24h: rainModule?.sum_rain_24,
              rain_60min: rainModule?.sum_rain_1,
              rain_live: rainModule?.rain,
              rainScale: this.weathermapFacade.rainScale(),

              wind_strength: windModule?.wind_strength,
              wind_angle: windModule?.wind_angle,
              gust_strength: windModule?.wind_gust,
              gust_angle: windModule?.wind_gust_angle,
              windType: this.weathermapFacade.windType(),
            },
          };
        }),
    });
  }

  filterStation(id: string): void {
    const filter = ['all', ZoomFilter, ['!=', ['get', 'id'], id]] as FilterSpecification;
    window.map.setFilter('stations', filter);
    window.map.setFilter('home-stations', filter);
    window.map.setFilter('friend-stations', filter);
    window.map.setFilter('favorite-stations', filter);
  }

  unfilterStation(): void {
    window.map.setFilter('stations', null);
    window.map.setFilter('home-stations', null);
    window.map.setFilter('friend-stations', null);
    window.map.setFilter('favorite-stations', null);
  }

  addSelectedLayer(): void {
    window.map.addSource('selected-station', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });

    window.map.addLayer(SelectedLayer);
  }

  addHomeLayer(): void {
    window.map.addSource('home-stations', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });

    window.map.addLayer(HomeLayers[this.weathermapFacade.selectedPintype()]);
  }

  addFriendLayer(): void {
    window.map.addSource('friend-stations', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });

    window.map.addLayer(FriendLayers[this.weathermapFacade.selectedPintype()]);
  }

  addFavoriteLayer(): void {
    window.map.addSource('favorite-stations', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });

    window.map.addLayer(FavoriteLayers[this.weathermapFacade.selectedPintype()]);
  }
}
